import { useQuery } from "@tanstack/react-query";
import type { CheckResult, DeployCheck, DeployCheckKind, DeployViewState, MissionDeploymentDTO } from "@bgreen/shared";
import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { get, post } from "../lib/api";
import { fmtDateTime, relTime, shortSha } from "../lib/format";
import { useAction } from "../lib/hooks";
import { EVENT_LABEL } from "../lib/labels";
import { Button } from "./ui/button";
import { Chip, Details, H2, Panel } from "./ui/primitives";

type Tone = "neutral" | "good" | "warn" | "bad" | "info";
export const DEPLOY_TONE: Record<DeployViewState, Tone> = {
  merged: "info", deploying: "info", deployed: "good", verified: "good", deployment_failed: "bad", unconfirmed: "warn",
  disabled: "neutral", not_configured: "neutral", not_merged: "neutral", closed_unmerged: "warn", untracked: "neutral",
};
const CHECK_LABEL: Record<DeployCheckKind, string> = { pipeline: "Pipeline", version: "Versión en ejecución", health: "Salud", smoke: "Prueba básica" };
const RESULT: Record<CheckResult, { label: string; tone: Tone; mark: string }> = {
  pass: { label: "superada", tone: "good", mark: "✓" },
  fail: { label: "fallo", tone: "bad", mark: "✕" },
  pending: { label: "pendiente", tone: "info", mark: "⟳" },
  unavailable: { label: "no disponible", tone: "neutral", mark: "–" },
  error: { label: "error al consultar", tone: "warn", mark: "!" },
};
const MATCH: Record<string, string> = { confirmed: "confirmada", mismatch: "no coincide", unconfirmed: "no confirmada" };

/**
 * Despliegue de la misión (Fase 5, solo observación): fusionada → desplegando → desplegada → verificada, o el fallo y su causa. Relaciona PR,
 * commit de merge y versión desplegada, con la fuente y la fecha de cada comprobación. No se pinta en misiones aún sin PR.
 */
export function DeploymentPanel({ missionId, hasPr }: { missionId: string; hasPr: boolean }) {
  const q = useQuery({ queryKey: ["mission-deployment", missionId], queryFn: () => get<MissionDeploymentDTO>(`/missions/${missionId}/deployment`), refetchInterval: 15_000, enabled: hasPr });
  const recheck = useAction(() => post(`/missions/${missionId}/deployment/recheck`), { ok: "Comprobación solicitada: se hará en el siguiente ciclo.", invalidate: [["mission-deployment", missionId]] });
  const v = q.data;
  if (!hasPr || !v || v.state === "not_merged") return null;
  const tracked = !!v.checks;
  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <H2 className="mb-0">Despliegue</H2>
        <span className="flex flex-wrap items-center gap-2">
          <Chip tone="neutral" title="La fábrica solo observa: no despliega, no fusiona y no cambia nada en el proveedor">Solo observación</Chip>
          <Chip tone={DEPLOY_TONE[v.state]}>{v.label}</Chip>
        </span>
      </div>
      <p className="m-0 mt-2 max-w-[75ch] text-[14px] text-mu">{v.detail}{v.reasonCode && tracked && <span className="ml-1.5 font-mono text-[11px]">({v.reasonCode})</span>}</p>

      {(v.mergeCommitSha || v.pullRequest) && (
        <div className="mt-3 grid gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-2">
          <Fact l="PR">{v.pullRequest ? <a href={v.pullRequest.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-ok hover:underline">#{v.pullRequest.number}<ExternalLink className="h-3 w-3" /></a> : "—"}</Fact>
          <Fact l="Commit fusionado"><span className="font-mono">{shortSha(v.mergeCommitSha)}</span>{v.mergedAt && <span className="text-mu"> · {fmtDateTime(v.mergedAt)}</span>}</Fact>
          {tracked && <Fact l={v.versionMatch === "mismatch" ? "Versión en ejecución" : "Versión desplegada"}>{v.deployedSha ? <><span className="font-mono">{shortSha(v.deployedSha)}</span><span className="text-mu"> · {MATCH[v.versionMatch ?? "unconfirmed"]}</span></> : <span className="text-mu">no confirmada</span>}</Fact>}
          {tracked && <Fact l="Entorno">{v.environment ?? "—"}{v.deploymentUrl && <a href={v.deploymentUrl} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-ok hover:underline">registro<ExternalLink className="h-3 w-3" /></a>}</Fact>}
        </div>
      )}

      {v.checks && (
        <div className="mt-3 flex flex-col border-t border-ln pt-2">
          {(Object.keys(CHECK_LABEL) as DeployCheckKind[]).map((k) => <CheckRow key={k} kind={k} c={v.checks![k]} />)}
        </div>
      )}

      {tracked && (
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[12px] text-mu">
          <span>observaciones {v.observations.used}/{v.observations.max}</span>
          {v.lastCheckedAt && <span>última {relTime(v.lastCheckedAt)}</span>}
          {v.nextCheckAt && <span>próxima {fmtDateTime(v.nextCheckAt)}</span>}
          {!v.finishedAt && v.deadlineAt && <span>ventana hasta {fmtDateTime(v.deadlineAt)}</span>}
        </div>
      )}

      {v.enabled && v.configured && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button disabled={!v.canRecheck} loading={recheck.isPending} onClick={() => recheck.mutate()}>Volver a comprobar</Button>
          {!v.canRecheck && v.recheckBlockedReason && <span className="text-[13px] text-mu">{v.recheckBlockedReason}</span>}
        </div>
      )}

      {v.history.length > 0 && (
        <Details title={`Historial (${v.history.length})`}>
          <div className="flex flex-col">{v.history.map((h, i) => (
            <div key={i} className="grid grid-cols-[88px_1fr] gap-2 border-t border-ln py-1.5 text-[13px] first:border-t-0">
              <span className="font-mono text-[11px] text-mu">{fmtDateTime(h.at)}</span>
              <span>{EVENT_LABEL[h.type] ?? h.type}{h.status && <span className="font-mono text-[12px]"> · {h.status}</span>}{h.detail && <span className="text-mu"> · {h.detail}</span>}<span className="ml-1 font-mono text-[10px] text-mu">[{h.actor}]</span></span>
            </div>))}
          </div>
        </Details>
      )}
    </Panel>
  );
}

const Fact = ({ l, children }: { l: string; children: ReactNode }) => <div className="flex min-w-0 gap-2"><span className="w-[130px] shrink-0 text-mu">{l}</span><span className="min-w-0 truncate">{children}</span></div>;

function CheckRow({ kind, c }: { kind: DeployCheckKind; c: DeployCheck }) {
  const r = RESULT[c.result];
  return (
    <div className="grid gap-x-3 gap-y-0.5 border-t border-ln py-2 text-[13px] first:border-t-0 sm:grid-cols-[170px_1fr]">
      <span className="flex items-center gap-2"><Chip tone={r.tone} className="h-6 min-w-[26px] justify-center px-1.5 text-[12px]" title={r.label}>{r.mark}</Chip><span className="font-medium">{CHECK_LABEL[kind]}</span></span>
      <span className="min-w-0">
        <span>{c.detail}</span>
        {(c.source || c.checkedAt) && <span className="block truncate font-mono text-[11px] text-mu" title={c.source ?? undefined}>{c.source ?? "sin fuente"}{c.checkedAt ? ` · ${fmtDateTime(c.checkedAt)}` : " · sin comprobar"}</span>}
      </span>
    </div>
  );
}
